import { getConfig } from './config.js';
import { BugsnagProvider } from './providers/BugsnagProvider.js';

/**
 * ErrorReporter Manager / Facade.
 * Coordinates the active reporting provider so that switching providers
 * (e.g. from Bugsnag to Firebase Crashlytics) can be done with zero impact on application code.
 */
class ErrorReporterService {
  constructor() {
    this.providers = new Map();
    this.activeProvider = null;
    this.config = null;
    this.isInitialized = false;

    // Register built-in providers
    this.register('bugsnag', new BugsnagProvider());
  }

  /**
   * Register a reporting provider.
   * @param {string} name
   * @param {ReporterProvider} provider
   */
  register(name, provider) {
    this.providers.set(name.toLowerCase(), provider);
    return this;
  }

  /**
   * Select and activate a provider by name.
   * @param {string} name
   */
  use(name) {
    const provider = this.providers.get(name.toLowerCase());
    if (!provider) {
      throw new Error(`Provider [${name}] is not registered in ErrorReporter.`);
    }
    this.activeProvider = provider;
    if (this.isInitialized && this.config) {
      this.activeProvider.init(this.config);
    }
    return this;
  }

  /**
   * Initialize ErrorReporter with the chosen provider (defaults to bugsnag).
   *
   * @param {Object} [customConfig]
   * @param {string} [providerName='bugsnag']
   */
  init(customConfig = {}, providerName = 'bugsnag') {
    this.config = getConfig(customConfig);
    this.isInitialized = true;

    if (!this.activeProvider) {
      this.use(providerName);
    } else {
      this.activeProvider.init(this.config);
    }

    return this;
  }

  /**
   * Report an error.
   * @param {Error|string} error
   * @param {Function|Object} [metadataOrOnError]
   */
  notify(error, metadataOrOnError) {
    if (!this.activeProvider) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[ErrorReporter (no active provider)]', error);
      }
      return;
    }
    this.activeProvider.notify(error, metadataOrOnError);
  }

  /**
   * Alias for notify to match common logging signatures.
   * @param {Error|string} error
   * @param {Object} [metadata]
   */
  logError(error, metadata) {
    this.notify(error, metadata);
  }

  /**
   * Set user information across reports.
   * @param {Object} user - { id, name, email }
   */
  setUser(user) {
    if (this.activeProvider) {
      this.activeProvider.setUser(user);
    }
  }

  /**
   * Clear identified user.
   */
  clearUser() {
    if (this.activeProvider) {
      this.activeProvider.clearUser();
    }
  }

  /**
   * Add custom metadata section.
   * @param {string} section
   * @param {Object} data
   */
  addMetadata(section, data) {
    if (this.activeProvider) {
      this.activeProvider.addMetadata(section, data);
    }
  }

  /**
   * Clear custom metadata section or key.
   * @param {string} section
   * @param {string} [key]
   */
  clearMetadata(section, key) {
    if (this.activeProvider) {
      this.activeProvider.clearMetadata(section, key);
    }
  }

  /**
   * Record a breadcrumb.
   * @param {string} name
   * @param {Object} [metadata]
   * @param {string} [type]
   */
  leaveBreadcrumb(name, metadata, type) {
    if (this.activeProvider) {
      this.activeProvider.leaveBreadcrumb(name, metadata, type);
    }
  }

  /**
   * Helper method to send a test error for health check / verification.
   */
  testError() {
    const error = new Error('[Bugsnag Verification] Frontend error reporting test');
    this.notify(error, {
      verification: {
        timestamp: new Date().toISOString(),
        environment: this.config ? this.config.releaseStage : 'unknown',
      },
    });
    if (typeof console !== 'undefined' && console.log) {
      console.log('[ErrorReporter] Test error reported to provider:', error);
    }
    return error;
  }

  /**
   * Get the active provider instance.
   */
  getActiveProvider() {
    return this.activeProvider;
  }

  /**
   * Get raw underlying provider client.
   */
  getClient() {
    return this.activeProvider ? this.activeProvider.getClient() : null;
  }
}

export const ErrorReporter = new ErrorReporterService();
export default ErrorReporter;



// WEBPACK FOOTER //
// ./resources/assets/js/monitoring/ErrorReporter.js